import { Component, OnInit, AfterViewInit, ViewChild } from "@angular/core";
import { AccountingMovementsService } from "./accounting-movements.service";
import { AccountMovementsViewModel } from "../models/accounting-movement.model";
import { AccountingManualFormComponent } from "../manual/accounting-manual-form.component";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, merge, of } from "rxjs";
import { catchError, map, startWith, switchMap } from "rxjs/operators";
import { UntypedFormControl, Validators } from "@angular/forms";
import { DummyComponent } from "../../../shared/dummy.component";
import { Router } from "@angular/router";
import { Utils } from "../../../shared/utils";
import * as FileSaver from 'file-saver';
import { AccountingAccountsDialogLedgerComponent } from "../accounting-accounts/accounting-accounts-dialog-ledger.component";
import { ExpensePDF, ResponseMovements } from "./responseMovements.model";
import { AccountingMovementsImportDialog } from "./import/accounting-movements-import-dialog";
import { AccountingImbalanceDialogComponent } from "../accounting-imbalance/accounting-imbalance-dialog.component";
import { consts } from "../../../consts";
import { AddFileService } from "../../../shared/components/add-file/add-file.service";
import { CoreComponent } from "../../../core/header/core.component";
import { Option } from "../../../shared/models/option.model";

@Component({
    selector: "convfact-accounting-movements-list",
    templateUrl: "./accounting-movements-list.component.html",
    styleUrls: ["./accounting-movements-list.component.css"],
    standalone: false
})
export class AccountingMovementsListComponent implements OnInit, AfterViewInit {

  // Table With Pagination
  public displayedColumns: string[] = ['accountNumber', 'description', 'reference', 'date', 'accountEntry', 'debit', 'credit', 'actions'];
  public data: AccountMovementsViewModel[] = [];

  public pageSizeDefault: number = 40;
  public resultsLength: number = 0;
  public isLoadingResults: boolean = true;

  @ViewChild(MatPaginator,{static: false}) paginator: MatPaginator;
  @ViewChild(MatSort,{static: false}) sort: MatSort;

  //Bandera que nos indica si hay asiento regularizado/cerrado/apertura, por lo tanto
  //deshabilitamos botón
  //false: Se puede realizar regularización/cierre/apertura
  //true: Ya hay regularización/cierre/apertura creada/o
  public spinnerReady: boolean = false;
  flagCheck: ResponseMovements;
  readyOptions: boolean = false;

  // Filter Pagination
  private filterMovementsClicked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public monthFromFilterPagination: UntypedFormControl;
  public monthToFilterPagination: UntypedFormControl;
  public globalFilterPagination: UntypedFormControl;

  public amountFilter: number;
  public descriptionFilter: string;
  public accountFilter: string;
  public dateFilter: Date;
  public seatFilter: string;

  public monthsOptions: Option[] = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
    .map((m, i) => ({ label: m, value: i }));


  public actionRowClickedId: string = "";

  formData: FormData;

  constructor(
    private accountingMovementService: AccountingMovementsService,
    public dialog: MatDialog,
    private dummy: DummyComponent,
    private router: Router,
    public snackBar: MatSnackBar,
    public addFileService: AddFileService,
    public core: CoreComponent,
  ) {
    this.setDefaultValesFormControl();
  }

  public ngOnInit() {
    this.accountingMovementService.checkCierreAperturaRegularizacion().then(response => {
      this.flagCheck = response;
      this.readyOptions = true;
    });
  }

  public ngAfterViewInit(): void {
    this.filterMovementsClicked.subscribe(() => (this.paginator.pageIndex = 0));
    this.listAccountMovements();
  }

  public downloadFilePdf(data: string): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    const splitText = data.split(',');
    const number = splitText.length;
    if (number == 3) {
      const expensePDF = new ExpensePDF();
      expensePDF.invoiceNumber = splitText[1].trim();
      expensePDF.issuerName = splitText[2].trim();
      this.accountingMovementService.getUidExpense(expensePDF).then(uid => {
        if (!uid) {
          this.displayInfo('No se ha encontrado el gasto para descargar su PDF.');
          return;
        }

        return this.addFileService.downloadFilePDF(uid, consts.INVOICE_ENTITY, consts.EMPTY_GUID).then(blob => {
          const fileURL = URL.createObjectURL(blob);
          window.open(fileURL, '_blank');
        });
      }).catch(() => this.displayInfo('Este gasto no tiene PDF adjunto o no se ha podido descargar.'));
    }
  }

  public importContabilidad(flag: boolean) {
    this.dialog
      .open(AccountingMovementsImportDialog, {
        width: '580px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          flag: flag
        }
      })
  }

  private setDefaultValesFormControl(): void {
    const initMonth: number = 0;
    const endMonth: number = 11;

    this.monthFromFilterPagination = new UntypedFormControl(initMonth.toString(), [Validators.required]);
    this.monthToFilterPagination = new UntypedFormControl(endMonth.toString(), [Validators.required]);

    const activities = this.core.activity;
    if (activities.length == 1) {
      this.globalFilterPagination = new UntypedFormControl(true, [Validators.requiredTrue]);
    } else {
      this.globalFilterPagination = new UntypedFormControl(false, [Validators.requiredTrue]);
    }
  }

  public listAccountMovements(): void {
    merge(this.paginator.page, this.filterMovementsClicked)
        .pipe(
          startWith({}),
          switchMap(() => {
            let monthFrom: number = this.monthFromFilterPagination.value;
            let monthTo: number = this.monthToFilterPagination.value;

            monthFrom++;
            monthTo++;

            this.isLoadingResults = true;
            return this.accountingMovementService.getAccountMovements(
              monthFrom,
              monthTo,
              this.amountFilter,
              this.descriptionFilter,
              this.accountFilter,
              this.seatFilter,
              this.dateFilter,
              this.paginator.pageIndex,
              this.pageSizeDefault,
              this.globalFilterPagination.value
            ).pipe(
              catchError(() => of(null))
            );
          }),
          map((data: any) => {
            this.isLoadingResults = false;

            if (data === null) {
              return [];
            }

            this.resultsLength = data.pages;
            return data.data;
          }),
        )
        .subscribe(data => {
          this.data = data;

          for (let i = 0; i < this.data.length; i++) {
            if (Utils.IsNullOrEmpty(this.data[i].reference) === true) {
              const searched = this.data.filter(x =>
                x.accountEntry === this.data[i].accountEntry &&
                Utils.IsNullOrEmpty(x.reference) === false
              );

              if (searched != null && searched.length > 0) {
                this.data[i].reference = searched[0].reference;
              }
            }
          }
        });
  }

  public addManualAccountMovements(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.dialog
      .open(AccountingManualFormComponent, {
        width: '60%',
        height: '70%',
        data: {
          accountingMovementId: ''
        }
      })
      .afterClosed().subscribe((result: string) => {
        if (!Utils.IsNullOrEmpty(result)) {
          if (result === 'OK') {
            this.dummy.redirectTo(this.router.url);
          }
        }
      });
  }

  public deleteManualAccountMovements(accountMovementId: string): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.accountingMovementService.deleteAccountMovementManual(accountMovementId)
      .then(response => {
        this.dummy.redirectTo(this.router.url);
      })
      .catch(err => {
        this.displayInfo("Ha ocurrido un error inesperado al eliminar el movimiento contable");
      });
  }

  public editManualAccountMovements(accountMovementId: string): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.dialog
      .open(AccountingManualFormComponent, {
        width: '60%',
        height: '70%',
        data: {
          accountingMovementId: accountMovementId
        }
      })
      .afterClosed().subscribe((result: string) => {
        if (!Utils.IsNullOrEmpty(result)) {
          if (result === 'OK') {
            this.dummy.redirectTo(this.router.url);
          }
        }
      });
  }

  public excelAccountMovements(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    let monthFrom: number = this.monthFromFilterPagination.value;
    let monthTo: number = this.monthToFilterPagination.value;

    monthFrom++; // Back is not Zero-Based;
    monthTo++; // Back is not Zero-Based

    this.accountingMovementService.getExcelAccountMovements(monthFrom, monthTo, this.globalFilterPagination.value)
      .then(response => {
        FileSaver.saveAs(response, Utils.GetExcelName("Movimientos contables"));
        this.isLoadingResults = false;
      }).catch(err => {
        this.displayInfo('Ha ocurrido un error inesperado al exportar el documento excel');
        this.isLoadingResults = false;
      });
  }

  public filterAccountMovements(): void {
    this.filterMovementsClicked.next(true);
  }

  public actionRowClicked(row: AccountMovementsViewModel): void {
    this.actionRowClickedId = row.id;
  }

  public ledger(id: number, name: string, number: string): void {
    this.dialog.open(AccountingAccountsDialogLedgerComponent, {
      width: '700px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        id: id,
        name: name,
        number: number
      },
    });
  }

  public displayInfo(message: string): void {
    const options = new MatSnackBarConfig();
    options.duration = 10000;
    options.panelClass = ['custom-snackbar', 'custom-snackbar-container']; this.snackBar.open(message, '', options);
  }

  public regularizacion(): void {

    if (this.flagCheck.regularizacion === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.regularizacion()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha realizado el asiento de regularización');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha realizado el asiento de regularización');

      });

    this.spinnerReady = false;
  }

  public undoRegularizacion(): void {

    if (this.flagCheck.undoRegularizacion === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.undoRegularizacion()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha eliminado el asiento de regularización');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha eliminado el asiento de regularización');

      });

    this.spinnerReady = false;
  }

  public cierre() {

    if (this.flagCheck.cierre === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.asientoCierre()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha realizado el asiento de cierre');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha realizado el asiento de cierre');

      });

    this.spinnerReady = false;
  }

  public undoCierre() {

    if (this.flagCheck.undoCierre === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.undoAsientoCierre()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha eliminado el asiento de cierre');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha eliminado el asiento de cierre');

      });

    this.spinnerReady = false;
  }

  public apertura() {

    if (this.flagCheck.apertura === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.asientoApertura()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha realizado el asiento de apertura');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha realizado el asiento de apertura');

      });

    this.spinnerReady = false;
  }

  public undoApertura() {

    if (this.flagCheck.undoApertura === false) {
      return;
    }

    this.spinnerReady = true;

    this.accountingMovementService.undoAsientoApertura()
      .then((response: boolean) => {

        if (response === true) {

          this.listAccountMovements();
          this.accountingMovementService.checkCierreAperturaRegularizacion().then(r => { this.flagCheck = r; });

        } else {

          this.displayInfo('No se ha eliminado el asiento de apertura');

        }

      })
      .catch(err => {

        this.displayInfo('No se ha eliminado el asiento de apertura');

      });

    this.spinnerReady = false;
  }

  public keyDownAccountNumberEnterPress(event: any): void {

    const eventNumber: string = event.target.value;

    if (eventNumber.includes('.') === true) {

      const splitedNumber: string[] = eventNumber.split('.');

      // Result from 600.1 must be...
      // [0] -> 600 - [1] -> 1

      const firstPartLength: number = splitedNumber[0].length; // 3

      const secondPartLength: number = splitedNumber[1].length; // 1

      const maxLength: number = 10; // 10

      const totalLength: number = firstPartLength + secondPartLength; // 4

      const operationResult: number = maxLength - totalLength; // 6

      let zerosToComplete: string = '';

      for (let i = 0; i < operationResult; i++) {
        zerosToComplete += '0';
      }

      const numberCreation: string[] = [];

      numberCreation[0] = splitedNumber[0];

      numberCreation[1] = zerosToComplete;

      numberCreation[2] = splitedNumber[1];

      const result: string = numberCreation.join('');

      if (result.length === 10) {
        this.accountFilter = result;
      }

      this.filterAccountMovements();

    }

  }

  public keyDownEnterPress(event: any): void {

    this.filterAccountMovements();

  }

  public excelAccountingImbalance(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.dialog.open(AccountingImbalanceDialogComponent, {
      width: '440px',
      maxWidth: '95vw',
    });
  }

  public trackByIndex(i: number) { return i; }

}

